import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Globe, Database, Server, Loader2, Check, Wand2, Rocket, ExternalLink, Copy, Paintbrush } from "lucide-react";

const TABS = [
  { id: "publish", name: "Publish", icon: Rocket },
  { id: "logo", name: "Logo", icon: Sparkles },
  { id: "platform", name: "Platform", icon: Paintbrush },
  { id: "domain", name: "Domain", icon: Globe },
  { id: "database", name: "Database", icon: Database },
  { id: "server", name: "Server", icon: Server },
];

export default function OwnerStudio({ open, onClose, projectId, project, onLogo, onRefresh }) {
  const [tab, setTab] = useState("publish");
  const [busy, setBusy] = useState(false);
  const [logoPrompt, setLogoPrompt] = useState("");
  const [logo, setLogo] = useState(project?.logo || "");
  const [provisions, setProvisions] = useState([]);
  const [domain, setDomain] = useState("");
  const [dbType, setDbType] = useState("PostgreSQL");
  const [dbName, setDbName] = useState("");
  const [srvProvider, setSrvProvider] = useState("AWS");
  const [srvRegion, setSrvRegion] = useState("us-east-1");
  const [srvSize, setSrvSize] = useState("small");
  const [extraCss, setExtraCss] = useState("");
  const [slug, setSlug] = useState(project?.slug || "");
  const liveUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/${projectId}`;

  useEffect(() => { setLogo(project?.logo || ""); setExtraCss(project?.overrides?.extra_css || ""); setSlug(project?.slug || ""); }, [project]);
  useEffect(() => {
    if (open && projectId) api.get(`/projects/${projectId}/provisions`).then((r) => setProvisions(r.data)).catch(() => {});
  }, [open, projectId]);

  const genLogo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/logo`, { prompt: logoPrompt });
      setLogo(data.logo); onLogo?.(data.logo); toast.success("Logo generated");
    } catch (e) { toast.error("Logo generation failed"); } finally { setBusy(false); }
  };
  const provision = async (kind, config) => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/provision`, { kind, config });
      const r = await api.get(`/projects/${projectId}/provisions`); setProvisions(r.data);
      toast.success(`${kind} configured`);
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const publishApp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/publish`);
      toast.success("Published live"); onRefresh?.();
      window.open(data.live_url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); } finally { setBusy(false); }
  };
  const unpublishApp = async () => {
    setBusy(true);
    try { await api.post(`/projects/${projectId}/unpublish`); toast.success("Taken offline"); onRefresh?.(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const saveCss = async () => {
    setBusy(true);
    try { await api.put(`/projects/${projectId}/overrides`, { extra_css: extraCss }); toast.success("Platform tweaks saved"); onRefresh?.(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const saveSlug = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/projects/${projectId}/slug`, { slug }); toast.success("Public link updated"); onRefresh?.(); setSlug(data.slug); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const inp = "w-full h-11 px-4 rounded-xl bg-slate-950 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl" data-testid="owner-studio">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-emerald-400" /> Owner Studio</DialogTitle></DialogHeader>
        <div className="flex gap-2 border-b border-slate-800 pb-3">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`studio-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm transition-colors ${tab === t.id ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:bg-slate-800/50"}`}>
              <t.icon className="h-4 w-4" /> {t.name}
            </button>
          ))}
        </div>

        <div className="py-2 min-h-[220px]">
          {tab === "publish" && (
            <div className="space-y-3" data-testid="studio-publish">
              <p className="text-sm text-slate-400">Publish this app to a real, shareable public URL served live by D Code.</p>
              {project?.published ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 dc-pulse" />
                    <span className="text-sm text-emerald-300">Live</span>
                    <a data-testid="live-url" href={liveUrl} target="_blank" rel="noreferrer" className="ml-2 text-sm text-cyan-400 hover:underline truncate flex-1">{liveUrl}</a>
                    <button onClick={() => { navigator.clipboard?.writeText(liveUrl); toast.success("Copied"); }} className="text-slate-400 hover:text-white"><Copy className="h-4 w-4" /></button>
                    <a href={liveUrl} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white"><ExternalLink className="h-4 w-4" /></a>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="republish" disabled={busy} onClick={publishApp} className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm disabled:opacity-50">Re-publish latest</button>
                    <button data-testid="unpublish" disabled={busy} onClick={unpublishApp} className="h-10 px-4 rounded-xl border border-slate-700 text-sm hover:text-rose-400 disabled:opacity-50">Take offline</button>
                  </div>
                </div>
              ) : (
                <button data-testid="publish-app" disabled={busy} onClick={publishApp}
                  className="h-11 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
                  {busy ? <><Loader2 className="h-4 w-4 dc-spin" /> Publishing...</> : <><Rocket className="h-4 w-4" /> Publish live</>}
                </button>
              )}
            </div>
          )}
          {tab === "platform" && (
            <div className="space-y-3" data-testid="studio-platform">
              <p className="text-sm text-slate-400">Extra CSS applied to THIS app only. Linked copies keep sharing the same core code — this just tweaks the look per platform.</p>
              <textarea data-testid="platform-css" value={extraCss} onChange={(e) => setExtraCss(e.target.value)} rows={6}
                placeholder={"/* e.g. make this copy's header dark */\nheader{background:#0b1220;color:#fff}"}
                className="w-full resize-none px-4 py-3 rounded-xl bg-slate-950 border border-slate-700 text-sm font-mono focus:border-emerald-500 focus:outline-none" />
              <button data-testid="save-platform-css" disabled={busy} onClick={saveCss}
                className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm disabled:opacity-50">Save tweaks</button>
            </div>
          )}
          {tab === "logo" && (
            <div className="space-y-3" data-testid="studio-logo">
              <p className="text-sm text-slate-400">Generate a real logo for your app with AI image generation.</p>
              <div className="flex gap-3 items-start">
                <div className="h-28 w-28 rounded-2xl border border-slate-700 bg-slate-950 flex items-center justify-center overflow-hidden shrink-0">
                  {logo ? <img src={logo} alt="logo" className="h-full w-full object-contain" /> : <Sparkles className="h-6 w-6 text-slate-600" />}
                </div>
                <div className="flex-1 space-y-2">
                  <input data-testid="logo-prompt" value={logoPrompt} onChange={(e) => setLogoPrompt(e.target.value)}
                    placeholder="Optional: describe the logo (e.g. minimalist coffee cup, emerald)" className={inp} />
                  <button data-testid="generate-logo" disabled={busy} onClick={genLogo}
                    className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
                    {busy ? <><Loader2 className="h-4 w-4 dc-spin" /> Generating (~1 min)...</> : <><Sparkles className="h-4 w-4" /> Generate logo</>}
                  </button>
                </div>
              </div>
            </div>
          )}
          {tab === "domain" && (
            <div className="space-y-4" data-testid="studio-domain">
              <div className="space-y-2">
                <p className="text-sm text-slate-400">Custom public link — sets a friendly slug for your live app URL.</p>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-slate-500 whitespace-nowrap">/api/public/</span>
                  <input data-testid="slug-input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="my-app" className={inp} />
                  <button data-testid="save-slug" disabled={busy || !slug} onClick={saveSlug}
                    className="h-11 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm whitespace-nowrap disabled:opacity-50">Save</button>
                </div>
              </div>
              <div className="space-y-2 border-t border-slate-800 pt-3">
                <p className="text-sm text-slate-400">Reserve a custom domain. After saving, point a CNAME to your live URL host.</p>
                <div className="flex gap-2">
                  <input data-testid="domain-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="myapp.com" className={inp} />
                  <button data-testid="provision-domain" disabled={busy || !domain} onClick={() => provision("domain", { domain })}
                    className="h-11 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm whitespace-nowrap disabled:opacity-50">Reserve</button>
                </div>
              </div>
            </div>
          )}
          {tab === "database" && (
            <div className="space-y-3" data-testid="studio-database">
              <p className="text-sm text-slate-400">Attach a database. Choose engine and name.</p>
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="db-type" value={dbType} onChange={(e) => setDbType(e.target.value)} className={inp}>
                  {["PostgreSQL", "MySQL", "SQLite", "MongoDB", "Redis"].map((d) => <option key={d}>{d}</option>)}
                </select>
                <input data-testid="db-name" value={dbName} onChange={(e) => setDbName(e.target.value)} placeholder="database name" className={inp} />
              </div>
              <button data-testid="provision-database" disabled={busy} onClick={() => provision("database", { engine: dbType, name: dbName })}
                className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm disabled:opacity-50">Attach database</button>
            </div>
          )}
          {tab === "server" && (
            <div className="space-y-3" data-testid="studio-server">
              <p className="text-sm text-slate-400">Provision a server for backend workloads.</p>
              <div className="grid grid-cols-3 gap-2">
                <select data-testid="srv-provider" value={srvProvider} onChange={(e) => setSrvProvider(e.target.value)} className={inp}>
                  {["AWS", "GCP", "Azure", "Fly.io", "Render"].map((d) => <option key={d}>{d}</option>)}
                </select>
                <input data-testid="srv-region" value={srvRegion} onChange={(e) => setSrvRegion(e.target.value)} placeholder="region" className={inp} />
                <select data-testid="srv-size" value={srvSize} onChange={(e) => setSrvSize(e.target.value)} className={inp}>
                  {["small", "medium", "large"].map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <button data-testid="provision-server" disabled={busy} onClick={() => provision("server", { provider: srvProvider, region: srvRegion, size: srvSize })}
                className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm disabled:opacity-50">Provision server</button>
            </div>
          )}

          {provisions.length > 0 && tab !== "logo" && (
            <div className="mt-4 space-y-1.5">
              <div className="text-xs uppercase tracking-wider text-slate-500">Configured</div>
              {provisions.filter((p) => p.kind === tab).map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-950/60 rounded-lg px-3 py-2">
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="font-mono text-xs">{JSON.stringify(p.config)}</span>
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
